import { RadioButtonWithLabelOption, TextLinkWidget } from '@tableau/widgets';
import React from 'react';

import { FormikCheckbox } from '~commonInfra/components/FormikCheckbox';
import { FormikRadioButtonGroup } from '~commonInfra/components/FormikRadioButtonGroup';
import { openReactConfirmationDialog } from '~commonInfra/dialogs/ReactConfirmationDialog';
import { TabDialogWidth } from '~commonInfra/dialogs/style/TabDialogWidth';
import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { HelpService } from '~commonInfra/services/HelpService';
import { SessionServerSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { Messages } from '~src/codegen/Localize';
import { SettingsStyles } from '~src/settings/styles/CommonStyles';

import { SettingsSection } from '../../../../components/common/SettingsSection';
import { learnMoreStyle, settingsRadioButtonItemHeight } from '../../../../styles/CommonStyles';
import { useSelector } from '../../../utils';
import { componentRegistryEntry } from '../../SettingsComponentRegistryFunctions';
import { SiteSettingsForSiteAdminProps } from '../../site-settings/ReactSiteSettingsForSiteAdmin';

export type FederatedProcessingSectionProps = SiteSettingsForSiteAdminProps<'flowFederationStrategySelectedConnectionEnabled' | 'alternativeFederationEngineEnabled'>;

export function FederatedProcessingSection(props: FederatedProcessingSectionProps) {
  const afeUiSettingEnabled = useSelector(SessionServerSelectors.isAfeUiSettingEnabled) && false;

  // Only render the section if inDatabaseProcessingEnabled feature flag is true
  if (!FeatureFlags.inDatabaseProcessingEnabled) {
    return null;
  }

  function openConfirmationDialog(event: React.ChangeEvent<HTMLInputElement>) {
    const isTurningOff = event.target.checked === false;

    if (isTurningOff && props.settings.flowFederationStrategySelectedConnectionEnabled) {
      const dialogPromiseResult = openReactConfirmationDialog({
        title: Messages.flowFederationStrategyAdminConfirmationDialogTitle(),
        message: Messages.flowFederationStrategyAdminConfirmationDialogMessage(),
        actionName: Messages.flowFederationStrategyAdminConfirmationDialogActionName(),
        action: () => Promise.resolve(),
        isActionDelete: true,
        dialogWidth: TabDialogWidth.width375px,
        paramsForAction: null,
      }).result;
      return dialogPromiseResult;
    }

    return Promise.resolve();
  }

  function getCrossDatabaseJoinsButtons(): RadioButtonWithLabelOption<boolean>[] {
    return [{
      testId: 'cross-database-joins-enabled-radio-button',
      label: Messages.crossDatabaseJoinsOn(),
      detail: Messages.crossDatabaseJoinsOnDescription(),
      value: false,
      disabled: !afeUiSettingEnabled
    }, {
      testId: 'cross-database-joins-disabled-radio-button',
      label: Messages.crossDatabaseJoinsOff(),
      detail: Messages.crossDatabaseJoinsOffDescription(),
      value: true,
      disabled: !afeUiSettingEnabled
    }];
  }

  return (
    <SettingsSection
      title={Messages.federatedProcessingTitle()}
      description=""
      testId={'federated-processing-setting'}
    >
            <div>
        {/* Cost-based optimization section */}
        <div style={{ marginBottom: '12px' }}>
          <h3 className={SettingsStyles.settingsGroupName} style={{ fontSize: '14px', marginBottom: '6px' }}>
            {Messages.flowFederationStrategyCostBasedOptimizationTitle()}
          </h3>
          <div className={SettingsStyles.settingsGroupDescription}>
            {Messages.flowFederationStrategyAdminDescription()}
          </div>
        </div>

        {/* Tableau Prep: Author-selected processing location */}
        <div style={{ marginBottom: '12px' }}>
          <h3 className={SettingsStyles.settingsGroupName} style={{ fontSize: '14px', marginBottom: '6px' }}>
            {Messages.flowFederationStrategyAuthorSelectedProcessingTitle()}
          </h3>
          <div className={SettingsStyles.checkboxGroup}>
            <FormikCheckbox
              name={'settings.flowFederationStrategySelectedConnectionEnabled'}
              label={Messages.flowFederationStrategyAdminCheckboxTitle()}
              beforeChange={openConfirmationDialog}
              testId={'flow-federation-strategy-admin-checkbox'}
            />
          </div>
          <div className={SettingsStyles.indent}>
            {Messages.flowFederationStrategyAdminCheckboxDescription()}
          </div>
        </div>

        {/* Tableau: Cross-database joins */}
        {FeatureFlags.alternativeFederationEngineUserInterface && (
          <div style={{ marginBottom: '12px' }}>
            <h3 className={SettingsStyles.settingsGroupName} style={{ fontSize: '14px', marginBottom: '6px' }}>
              {Messages.crossDatabaseJoins()}
            </h3>
            <div className={SettingsStyles.settingsGroupDescription} style={{ marginBottom: '6px' }}>
              <span data-tb-test-id='cross-database-joins-description'>
                {Messages.crossDatabaseJoinsMsg()}
              </span>
              <div>
                <TextLinkWidget
                  testId={'cross-database-joins-learn-more-link'}
                  className={learnMoreStyle}
                  href={HelpService.getServerHelpUrlByCategory('cross-dbase-join-admin')}
                  target={'_blank'}
                >
                  {Messages.learnMore()}
                </TextLinkWidget>
              </div>
            </div>
            <div className={SettingsStyles.checkboxGroup}>
              <FormikRadioButtonGroup
                name={'settings.alternativeFederationEngineEnabled'}
                buttonList={getCrossDatabaseJoinsButtons()}
                testId={'cross-database-joins-options'}
                className={settingsRadioButtonItemHeight}
                aria-label={Messages.crossDatabaseJoins()}
              />
            </div>
          </div>
        )}
      </div>
    </SettingsSection>
  );
}

export const FederatedProcessingSectionSpecs = componentRegistryEntry({
  name: 'FederatedProcessingSection',
  component: FederatedProcessingSection
});